import { createInspectToolbar } from "../utils/inspect-toolbar";
import nextTick from "../utils/next-tick";
import { getArtworkInfo } from "../utils/artwork-model-utils";
import { findAncestorWithComponent } from "../utils/scene-graph";

import ThreeMeshUI from "three-mesh-ui";
import FontJSON from "../assets/fonts/Roboto-msdf.json";
import FontImage from "../assets/fonts/Roboto-msdf.png";
import { ASCI_CHARACTERS } from "../react-components/constants/artworks";

const cleanText = text => {
  if (!text) return "";
  const validCharacters = ASCI_CHARACTERS;
  return text.replace(new RegExp(`[^${validCharacters}]`, "g"), "");
};

/**
 * Calculate maximum character length for a text field based on font size and container dimensions
 * @param {number} containerWidth - Width of the text container
 * @param {number} fontSize - Font size in same units as container width
 * @param {number} lines - Number of lines available
 * @returns {number} Maximum character count
 */
const calculateMaxCharLength = (containerWidth, fontSize, lines) => {
  // Approximate character width ratio (monospace equivalent)
  // For proportional fonts, average character width is roughly 0.6 * fontSize
  const avgCharWidth = fontSize * 0.6;
  const charsPerLine = Math.floor(containerWidth / avgCharWidth);
  return charsPerLine * lines;
};

AFRAME.registerComponent("artwork-info", {
  schema: {
    artworkInfo: { type: "string" },
    mediumName: { type: "string" },
    isDefaultMedium: { type: "boolean" },
    image: { type: "string" },
    hideArtworkLabel: { type: "boolean", default: false }
  },
  tick(time) {
    ThreeMeshUI.update();

    const now = this.getCurrentTime(time);
    if (this.shouldUpdateDimensions(now)) {
      this.refreshDimensionsAndPosition();
    }

    // Check and update inverse scale only when parent scale changes
    if (this.needsScaleUpdate()) {
      this.updateInverseScale();
    }
  },
  init() {
    this.modelLoaded = false;
    this.cardInfoWidth = null;
    this.lastParentScale = null; // Track last known scale
    this.artworkParent = null; // Cache parent reference (set later when available)
    this.basePosition = null; // Store the original intended position (before any scaling)
    this.worldOffset = null; // Store the world-space offset from artwork edge
    this.artworkDimensionsLocal = null; // Cache artwork dimensions in local space
    this.artworkScaledListenerAttached = false;
    this.onArtworkScaled = this.handleArtworkScaled.bind(this);
    this.dimensionUpdateMs = 1000;
    this.lastDimensionsUpdate = 0;
    this.labelVisible = true; // Track if label is visible
    this.labelContainer = null;
    this.infoButton = null; // Reference to info button
    this.inspectControls = null;
    this.backgroundButtonText = null;
    this.modeScene = null;
    this.modeEventsAttached = false;
    this.modeEventHandler = null;

    // Cache label constants (avoid recalculating in tick)
    this.labelHeight = 0.51; // Increased to accommodate 3-line description
    this.labelWidth = 0.9;
    this.labelHalfWidth = 0.45;

    // Hide the component initially until dimensions are ready
    this.el.object3D.visible = false;

    // Hide the component if hideArtworkLabel is true
    if (this.data.hideArtworkLabel) {
      return; // Don't proceed with scene creation if hidden
    }
  },
  update(oldData) {
    if (this.data.artworkInfo !== oldData.artworkInfo) {
      this.modelLoaded = false;
      // Hide while recreating
      this.el.object3D.visible = false;
      this.waitForValidDimensionsAndCreateScene();
    }
    this.handlePresentedArtwork();
  },
  remove() {
    if (this.artworkParent && this.artworkScaledListenerAttached) {
      this.artworkParent.removeEventListener("artwork-scaled", this.onArtworkScaled);
      this.artworkScaledListenerAttached = false;
    }
    this.teardownModeListeners();
    this.inspectToolbar?.dispose();
  },
  handlePresentedArtwork() {
    const ancestorEl = findAncestorWithComponent(this.el, "artwork");
    if (ancestorEl) {
      ancestorEl.components["artwork"].handlePresentedArtwork();
    }
  },
  getCurrentTime(time) {
    if (typeof time === "number") return time;
    if (typeof performance !== "undefined" && typeof performance.now === "function") {
      return performance.now();
    }
    return Date.now();
  },
  shouldUpdateDimensions(now) {
    if (this.data.hideArtworkLabel) return false;
    if (!this.dimensionUpdateMs) return false;
    if (!this.lastDimensionsUpdate || now - this.lastDimensionsUpdate >= this.dimensionUpdateMs) {
      this.lastDimensionsUpdate = now;
      return true;
    }
    return false;
  },
  refreshDimensionsAndPosition() {
    if (this.data.hideArtworkLabel) return;
    if (!this.setupArtworkParent()) return;

    const parentScale = this.artworkParent?.object3D?.scale;
    if (!parentScale) return;

    const dimensionsWorld = this.getArtworkDimensions();
    if (dimensionsWorld) {
      this.artworkDimensionsLocal = this.convertWorldDimensionsToLocal(dimensionsWorld, parentScale);
    }

    if (this.worldOffset) {
      this.updateLabelPosition();
    }
  },
  needsScaleUpdate() {
    if (!this.setupArtworkParent()) return false;

    const parentScale = this.artworkParent.object3D.scale;

    if (!this.lastParentScale) return true;

    const tolerance = 0.001;
    return (
      Math.abs(parentScale.x - this.lastParentScale.x) > tolerance ||
      Math.abs(parentScale.y - this.lastParentScale.y) > tolerance ||
      Math.abs(parentScale.z - this.lastParentScale.z) > tolerance
    );
  },
  updateInverseScale() {
    if (!this.setupArtworkParent()) return;

    const parentScale = this.artworkParent.object3D.scale;

    // Apply inverse scale to the label's scale (to keep text size constant)
    this.el.object3D.scale.set(1 / parentScale.x, 1 / parentScale.y, 1 / parentScale.z);

    this.updateLabelPosition();

    // Store current scale for next comparison
    this.lastParentScale = { x: parentScale.x, y: parentScale.y, z: parentScale.z };
  },
  setupArtworkParent() {
    const parent = findAncestorWithComponent(this.el, "artwork");

    if (!parent) {
      return false;
    }

    if (this.artworkParent !== parent) {
      if (this.artworkParent && this.artworkScaledListenerAttached) {
        this.artworkParent.removeEventListener("artwork-scaled", this.onArtworkScaled);
      }

      this.artworkParent = parent;
      this.artworkParent.addEventListener("artwork-scaled", this.onArtworkScaled);
      this.artworkScaledListenerAttached = true;
      this.lastParentScale = null; // Force inverse scale recalculation for new parent reference
    }

    return true;
  },
  handleArtworkScaled() {
    if (this.data.hideArtworkLabel) return;

    const parentScale = this.artworkParent?.object3D?.scale || { x: 1, y: 1, z: 1 };
    const dimensionsWorld = this.getArtworkDimensions();
    if (dimensionsWorld) {
      this.artworkDimensionsLocal = this.convertWorldDimensionsToLocal(dimensionsWorld, parentScale);
    }
    this.lastParentScale = null;
    this.lastDimensionsUpdate = 0;
    this.updateInverseScale();
  },
  waitForValidDimensionsAndCreateScene() {
    let attempts = 0;
    const maxAttempts = 30; // 3 seconds max (30 * 100ms)
    let lastDimensions = null;
    let stableCount = 0;
    const requiredStableChecks = 2; // Dimensions must be stable for 2 checks (200ms)

    const checkDimensions = () => {
      attempts++;
      const dimensions = this.getArtworkDimensions();

      if (dimensions && dimensions.width > 0.01 && dimensions.height > 0.01) {
        // Check if dimensions are stable (unchanged from last check)
        if (lastDimensions) {
          const widthDiff = Math.abs(dimensions.width - lastDimensions.width);
          const heightDiff = Math.abs(dimensions.height - lastDimensions.height);
          const tolerance = 0.001; // 1mm tolerance

          if (widthDiff < tolerance && heightDiff < tolerance) {
            stableCount++;

            if (stableCount >= requiredStableChecks) {
              this.createScene();
              return;
            }
          } else {
            stableCount = 0;
          }
        }

        lastDimensions = { ...dimensions };

        if (attempts < maxAttempts) {
          setTimeout(checkDimensions, 100);
        } else {
          console.warn("[ArtworkInfo] Max attempts reached, creating scene with current dimensions");
          this.createScene();
        }
      } else if (attempts < maxAttempts) {
        // Retry after a short delay
        stableCount = 0;
        lastDimensions = null;
        setTimeout(checkDimensions, 100);
      } else {
        console.error("[ArtworkInfo] Max attempts reached, label may not display correctly");
      }
    };

    checkDimensions();
  },
  getArtworkDimensions() {
    if (!this.setupArtworkParent()) return null;

    const artwork = this.artworkParent;
    if (!artwork) return null;

    // Find the medium entity (child with artwork-medium component)
    let mediumEntity = null;
    const children = artwork.children || [];
    for (let i = 0; i < children.length; i++) {
      if (children[i].components && children[i].components["artwork-medium"]) {
        mediumEntity = children[i];
        break;
      }
    }

    // If no medium, fall back to artwork mesh (for backward compatibility)
    const artworkMesh = mediumEntity ? mediumEntity.object3DMap.mesh : artwork.object3DMap.mesh;
    if (!artworkMesh) return null;

    // Calculate accurate dimensions using vertex positions (camera-framing-utils.js approach)
    // This works for both planes and complex 3D models
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let hasGeometry = false;

    const vertex = new THREE.Vector3();
    const parentObj = artwork.object3D;
    parentObj.updateMatrixWorld(true);
    const parentInverse = new THREE.Matrix4().copy(parentObj.matrixWorld).invert();
    const toParentLocal = new THREE.Matrix4();

    artworkMesh.updateMatrixWorld(true);

    artworkMesh.traverse(child => {
      if (child.isMesh && child.geometry) {
        const positions = child.geometry.attributes.position;

        if (positions) {
          hasGeometry = true;
          toParentLocal.multiplyMatrices(parentInverse, child.matrixWorld);
          for (let i = 0; i < positions.count; i++) {
            vertex.fromBufferAttribute(positions, i);
            // Transform to parent local space
            vertex.applyMatrix4(toParentLocal);

            // Track min/max for width and height in parent local space
            minX = Math.min(minX, vertex.x);
            maxX = Math.max(maxX, vertex.x);
            minY = Math.min(minY, vertex.y);
            maxY = Math.max(maxY, vertex.y);
          }
        }
      }
    });

    if (!hasGeometry) return null;

    // Calculate dimensions from actual vertex extents (in parent local space)
    const localWidth = maxX - minX;
    const localHeight = maxY - minY;
    const parentScale = parentObj.scale;
    const width = Math.abs(localWidth * (parentScale.x || 1));
    const height = Math.abs(localHeight * (parentScale.y || 1));

    // Check if there's a frame (child of artwork) and add its thickness
    let frameThickness = 0;
    const artworkChildren = artwork.children || [];
    for (let i = 0; i < artworkChildren.length; i++) {
      const child = artworkChildren[i];
      if (child.components && child.components["artwork-frame"]) {
        const frameModel = child.getObject3D("mesh");
        if (frameModel && child.object3D.visible) {
          // Measure the frame by checking one of its corner pieces
          frameModel.traverse(node => {
            if (node.isMesh && node.name === "corner_top_right") {
              if (!node.geometry.boundingBox) {
                node.geometry.computeBoundingBox();
              }
              const bbox = node.geometry.boundingBox;
              if (bbox) {
                // Frame thickness is approximately the corner width/2
                // Corners are positioned at artwork edges, so they extend outward
                frameThickness = ((bbox.max.x - bbox.min.x) * node.scale.x) / 2;
              }
            }
          });
        }
        break;
      }
    }

    // Add frame thickness to both sides
    const totalWidth = width + frameThickness * 2;
    const totalHeight = height + frameThickness * 2;

    return { width: totalWidth, height: totalHeight, frameThickness };
  },
  async createScene() {
    const artworkInfo = getArtworkInfo({
      medium: this.data.artworkInfo?.medium || this.data.mediumName,
      ...this.data.artworkInfo
    });

    this.artworkInfo = artworkInfo;

    const { author, description, title, artworkDate, medium, metadata } = this.artworkInfo;
    const dimensions = this.getDisplayDimensions();

    // Calculate gap from artwork edge (world space constant)
    // This is just the small gap, not including the label width
    let gapFromEdge = 0.15; // 15cm gap in world space
    if (this.data.isDefaultMedium) {
      gapFromEdge += 0.05; // Extra 5cm for default medium
    }

    // Store the world-space gap from the edge (not including label width)
    this.worldOffset = {
      x: gapFromEdge,
      y: 0, // Y is calculated from geometry, not an offset
      z: 0.02
    };
    this.lastDimensionsUpdate = 0;

    const boxEntity = document.createElement("a-entity");
    boxEntity.setAttribute("class", "artwork-label-container");
    this.el.appendChild(boxEntity);
    this.labelContainer = boxEntity;

    const container = new ThreeMeshUI.Block({
      width: 0.9,
      height: 0.51, // Increased to accommodate 3-line description
      backgroundColor: new THREE.Color(0xffffff),
      justifyContent: "start",
      textAlign: "left",
      fontFamily: FontJSON,
      fontTexture: FontImage,
      backgroundOpacity: 1, // Main container has white background
      hiddenOverflow: true
    });

    container.setupState({
      state: "hidden-on",
      attributes: { hiddenOverflow: true }
    });

    container.setState("hidden-on");

    // Dynamic character limits based on font size and container width
    const containerWidth = 0.86;
    const titleMaxLength = calculateMaxCharLength(containerWidth, 0.035, 1); // 1 line for title
    const descriptionMaxLength = calculateMaxCharLength(containerWidth, 0.03, 3); // 3 lines for description
    const otherFieldsMaxLength = calculateMaxCharLength(containerWidth, 0.03, 1); // 1 line for medium/dimensions

    // Author: single line with fixed character limit
    const truncatedAuthor = author?.length > 37 ? author?.substring(0, 37) + "..." : author;

    const truncatedArtTitlte = title?.length > titleMaxLength ? title?.substring(0, titleMaxLength - 3) + "..." : title;

    // Custom medium: single line with truncation
    const customMedium =
      medium == "Custom" && metadata?.customMedium
        ? metadata.customMedium.length > otherFieldsMaxLength
          ? metadata.customMedium.substring(0, otherFieldsMaxLength - 3) + "..."
          : metadata.customMedium
        : null;

    const authorContainer = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.08, // Single line for author
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundOpacity: 0 // Transparent - use parent's background
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedAuthor),
        fontSize: 0.04,
        fontColor: new THREE.Color(0x000000)
      })
    );

    const artTitleContainer = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.07,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundOpacity: 0 // Transparent - use parent's background
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedArtTitlte),
        fontSize: 0.035,
        fontColor: new THREE.Color(0x000000)
      })
    );

    const artDateContainer = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundOpacity: 0 // Transparent - use parent's background
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(artworkDate),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    const artMedium = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundOpacity: 0 // Transparent - use parent's background
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(customMedium || medium?.label || medium),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    const truncatedDimensions =
      dimensions?.length > otherFieldsMaxLength
        ? dimensions.substring(0, otherFieldsMaxLength - 3) + "..."
        : dimensions;

    const artDimensions = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.05,
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      backgroundOpacity: 0 // Transparent - use parent's background
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedDimensions),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    // Description: supports 3 lines (calculated dynamically)
    const truncatedDescription =
      description?.length > descriptionMaxLength
        ? description?.substring(0, descriptionMaxLength - 3) + "..."
        : description;

    const artDescription = new ThreeMeshUI.Block({
      width: 0.86,
      height: 0.15, // Increased to provide more space for 3 lines of description
      padding: 0.05,
      justifyContent: "start",
      textAlign: "left",
      hiddenOverflow: true,
      backgroundOpacity: 0
    }).add(
      new ThreeMeshUI.Text({
        content: cleanText(truncatedDescription),
        fontSize: 0.03,
        fontColor: new THREE.Color(0x333333)
      })
    );

    container.add(authorContainer, artTitleContainer);
    if (artworkDate) {
      container.add(artDateContainer);
    }

    if (medium) {
      container.add(artMedium);
    }
    if (dimensions) {
      container.add(artDimensions);
    }
    if (description) {
      container.add(artDescription);
    }

    boxEntity.setObject3D("mesh", container);

    // Only show the white label in the XR Inspect Area.
    this.updateModeVisibility();

    const parentScale = this.artworkParent?.object3D?.scale || { x: 1, y: 1, z: 1 };
    const dimensionsWorld = this.getArtworkDimensions();
    if (dimensionsWorld) {
      this.artworkDimensionsLocal = this.convertWorldDimensionsToLocal(dimensionsWorld, parentScale);
    } else {
      this.artworkDimensionsLocal = null;
    }
    this.basePosition = null;

    this.updateLabelPosition();

    await nextTick();

    // Create info button instead of showing label directly
    if (!this.data.hideArtworkLabel) {
      this.createInspectControls();
      this.createInfoButton();
      this.lastParentScale = null;
      this.updateInverseScale(); // Apply initial inverse scale and finalize position
    }

    this.setupModeListeners();
    this.updateModeVisibility();
    this.handlePresentedArtwork();
  },
  createInspectControls() {
    if (this.inspectControls) return;
    const controls = createInspectToolbar({
      isActive: () => this.isImmersiveMode() && this.isInspectingThisArtwork(),
      onToggle: () => this.modeScene.systems["hubs-systems"].cameraSystem.toggleLights(),
      onClose: () => {
        this.modeScene.emit("uninspect");
        this.modeScene.systems["hubs-systems"].cameraSystem.uninspect();
      }
    });
    this.inspectToolbar = controls;
    controls.el.object3D.position.y = -this.labelHeight / 2 - 0.15;
    this.backgroundButtonText = controls.caption;
    this.backgroundIconSlash = controls.slash;
    this.inspectControlIcons = controls.icons;
    this.el.appendChild(controls.el);
    this.inspectControls = controls.el;
  },
  getInspectBounds() {
    // Include the fixed label and toolbar footprint before asynchronous text layout finishes.
    this.el.object3D.updateWorldMatrix(true, false);
    return new THREE.Box3(
      new THREE.Vector3(-this.labelWidth / 2, -this.labelHeight / 2 - 0.25, 0),
      new THREE.Vector3(this.labelWidth / 2, this.labelHeight / 2, 0.03)
    ).applyMatrix4(this.el.object3D.matrixWorld);
  },
  isInspectingThisArtwork() {
    if (!this.isInspectMode()) return false;
    const scene = this.modeScene || AFRAME.scenes[0];
    const target = scene?.systems["hubs-systems"]?.cameraSystem.inspectable;
    if (!target) return false;
    for (let object = this.el.object3D; object; object = object.parent) {
      if (object === target) return true;
    }
    return false;
  },
  createInfoButton() {
    // Get the artwork parent element for reference
    const ancestorEl = findAncestorWithComponent(this.el, "artwork");
    if (!ancestorEl) {
      console.warn("[artwork-info] Cannot create info button - artwork parent not found");
      return;
    }
    // Position calculation
    if (!this.setupArtworkParent()) {
      console.warn("[artwork-info] setupArtworkParent failed");
      return;
    }

    // Create button entity
    const buttonEntity = document.createElement("a-entity");
    buttonEntity.setAttribute("class", "ui artwork-info-button");
    buttonEntity.setAttribute("mixin", "rounded-action-button");
    buttonEntity.setAttribute("is-remote-hover-target", "");
    buttonEntity.setAttribute("tags", { singleActionButton: true });
    buttonEntity.setAttribute("slice9", "side: front");
    const buttonMargin = -0.1;
    const buttonHeight = 0.2;
    const verticalOffset = -this.labelHeight / 2 + buttonHeight / 2;
    buttonEntity.object3D.position.set(-this.labelHalfWidth - buttonMargin, verticalOffset, 0);

    // Create icon inside button
    const iconEntity = document.createElement("a-entity");
    iconEntity.setAttribute("sprite", "name: info.png; singleSided: true");
    iconEntity.setAttribute("icon-button", "image: info.png; hoverImage: info.png;");
    iconEntity.setAttribute("scale", "0.100 0.100 0.100");
    iconEntity.setAttribute("position", "0 0 0.001");

    buttonEntity.appendChild(iconEntity);

    // Append button first
    this.el.appendChild(buttonEntity);
    this.infoButton = buttonEntity;
    this.updateModeVisibility();

    // Wait for the button to be initialized by A-Frame and registered in BitECS
    buttonEntity.addEventListener("loaded", () => {
      // Store button parent for later restoration
      const buttonParentEl = this.el;

      // Listen for uninspect to restore button
      const onUninspect = () => {
        if (this.infoButton && !this.infoButton.parentNode) {
          buttonParentEl.appendChild(this.infoButton);
        }
      };

      AFRAME.scenes[0].addEventListener("uninspect", onUninspect);

      // Now add interact handler after button is registered
      buttonEntity.object3D.addEventListener("interact", () => {
        // XR uses the white label in Inspect; desktop uses the 2D panel.
        window.APP.scene.systems["hubs-systems"].cameraSystem.inspectArtwork(ancestorEl, 1.5);
        if (!this.isImmersiveMode()) {
          window.dispatchEvent(new CustomEvent("open-artwork-info", { detail: ancestorEl }));
        }
      });
    });

    // Make parent visible to show button
    this.el.object3D.visible = true;
  },
  updateLabelPosition() {
    if (!this.worldOffset) return;

    if (!this.setupArtworkParent()) return;

    const parentScale = this.artworkParent.object3D.scale;

    const dimensionsWorld = this.getWorldDimensionsFromLocal(parentScale);
    if (!dimensionsWorld) return;

    const halfWidthWorld = dimensionsWorld.width / 2;
    const halfHeightWorld = dimensionsWorld.height / 2;

    const worldX = halfWidthWorld + this.worldOffset.x + this.labelHalfWidth;
    const worldY = -halfHeightWorld + this.labelHeight / 2;
    const worldZ = this.worldOffset.z;

    const localX = worldX / (parentScale.x || 1);
    const localY = worldY / (parentScale.y || 1);
    const localZ = worldZ / (parentScale.z || 1);

    this.el.object3D.position.set(localX, localY, localZ);
    this.basePosition = { x: localX, y: localY, z: localZ };
  },
  convertWorldDimensionsToLocal(dimensionsWorld, parentScale) {
    return {
      width: dimensionsWorld.width / (parentScale.x || 1),
      height: dimensionsWorld.height / (parentScale.y || 1),
      depth: dimensionsWorld.depth ? dimensionsWorld.depth / (parentScale.z || 1) : null
    };
  },
  getWorldDimensionsFromLocal(parentScale) {
    if (this.artworkDimensionsLocal) {
      return {
        width: this.artworkDimensionsLocal.width * (parentScale.x || 1),
        height: this.artworkDimensionsLocal.height * (parentScale.y || 1),
        depth: this.artworkDimensionsLocal.depth ? this.artworkDimensionsLocal.depth * (parentScale.z || 1) : null
      };
    }

    return this.getArtworkDimensions();
  },
  getDisplayDimensions() {
    const info = this.artworkInfo || {};

    const normalize = value => {
      if (value === null || value === undefined || value === "") return null;
      const num = Number(value);
      if (!Number.isFinite(num)) return null;
      const rounded = Math.round(num * 100) / 100;
      return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(2).replace(/\.?0+$/, "");
    };

    const width = normalize(info.dimensionsX);
    const height = normalize(info.dimensionsY);
    const depth = normalize(info.dimensionsZ);

    // Museum convention: HEIGHT × WIDTH × DEPTH
    const parts = [];
    if (height) parts.push(height);
    if (width) parts.push(width);
    if (depth) parts.push(depth);

    if (parts.length > 0) {
      return `${parts.join(" x ")} in`;
    }

    if (typeof info.dimensions === "string" && info.dimensions.trim().length > 0) {
      return info.dimensions;
    }

    return "";
  },
  isImmersiveMode() {
    const scene = AFRAME.scenes && AFRAME.scenes[0];
    if (!scene) return false;
    return scene.is("vr-mode") || scene.is("ar-mode");
  },
  isInspectMode() {
    const scene = AFRAME.scenes && AFRAME.scenes[0];
    if (!scene) return false;
    return scene.is("inspect-mode");
  },
  setupModeListeners() {
    if (this.modeEventsAttached) return;
    const scene = AFRAME.scenes && AFRAME.scenes[0];
    if (!scene) return;
    this.modeScene = scene;
    this.modeEventHandler = () => {
      this.updateModeVisibility();
    };
    [
      "enter-vr",
      "exit-vr",
      "enter-ar",
      "exit-ar",
      "inspect-mode-changed",
      "inspect-target-changed",
      "inspect-lights-changed"
    ].forEach(evt => {
      scene.addEventListener(evt, this.modeEventHandler);
    });
    this.modeEventsAttached = true;
  },
  teardownModeListeners() {
    if (!this.modeEventsAttached || !this.modeScene || !this.modeEventHandler) return;
    [
      "enter-vr",
      "exit-vr",
      "enter-ar",
      "exit-ar",
      "inspect-mode-changed",
      "inspect-target-changed",
      "inspect-lights-changed"
    ].forEach(evt => {
      this.modeScene.removeEventListener(evt, this.modeEventHandler);
    });
    this.modeScene = null;
    this.modeEventHandler = null;
    this.modeEventsAttached = false;
  },
  updateModeVisibility() {
    const immersive = this.isImmersiveMode();
    const inspecting = this.isInspectMode();
    const showInspectUI = immersive && this.isInspectingThisArtwork();
    if (this.labelContainer) {
      this.labelContainer.object3D.visible = showInspectUI;
    }
    if (this.inspectControls) {
      this.inspectControls.object3D.visible = showInspectUI;
    }
    if (this.backgroundButtonText) {
      const lightsEnabled = this.modeScene?.systems["hubs-systems"]?.cameraSystem.lightsEnabled;
      this.backgroundButtonText.setAttribute("text", "value", lightsEnabled ? "Focus view" : "Show Verse");
      if (this.backgroundIconSlash) this.backgroundIconSlash.visible = !lightsEnabled;
    }
    if (this.infoButton) {
      this.infoButton.object3D.visible = !inspecting;
    }
  }
});
